import React from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { CaretRightIcon, CertificateIcon, GraduationCapIcon } from "@phosphor-icons/react";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";

export default function ProgressTab({ integrationId, hasDegrees, degreeName, progressStats, coursesData }) {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const handleContinueCoursePress = (id: string) => {
    navigation.navigate("Custom Course Student", {
      course_id: id,
      integration_id: integrationId,
    });
  };

  return (
    <ScrollView style={{ flex: 1 }} tabIndex={0}>
      {hasDegrees && (
        <View style={styles.degreeProgressCard}>
          <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
            <CertificateIcon size={24} color="#D80F7C" />
            <CustomText style={styles.degreeName} text={degreeName} useTranslationText={false} />
          </View>
          <View style={styles.overallProgressContainer}>
            <CustomText size="s" style={styles.overallProgress} text="Overall Progress" useTranslationText={false} />
            <CustomText
              size="s"
              style={styles.overallProgressValue}
              text={`${progressStats.overall || 0}%`}
              useTranslationText={false}
            />
          </View>
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBar, { width: `${progressStats.overall || 0}%` }]} />
          </View>
          <CustomText
            size="s"
            style={styles.overallProgress}
            text="courses_remaining"
            interpolation={{ remainingCourses: progressStats.totalCoreCourses - progressStats.coreCourses || 0 }}
          />
        </View>
      )}

      {coursesData.length > 0 && (
        <View>
          <View style={styles.enrolled}>
            <GraduationCapIcon size={24} color="black" />
            <CustomText size="m" weight="bold" text="Currently Enrolled" style={{ marginLeft: 8 }} />
          </View>
          {coursesData.map((course) => (
            <View key={course.course.id} style={styles.courseCard}>
              <View>
                <CustomText size="s" useTranslationText={false} text={course.course.name} style={styles.courseName} />
                <View style={styles.courseDetails}>
                  <View style={[styles.progressBarContainer, styles.progressBarContainerSmall]}>
                    <View style={[styles.progressBar, { width: `${course.progress}%` }]} />
                  </View>
                  {course.progress > 0 ? (
                    <CustomText
                      size="s"
                      text="course_complete"
                      interpolation={{ percentage: course.progress }}
                      style={styles.overallProgress}
                    />
                  ) : (
                    <CustomText size="s" text="Not Started" style={styles.overallProgress} />
                  )}
                </View>
              </View>
              <CustomButton
                onPress={() => handleContinueCoursePress(course.course.id)}
                text={null}
                leftIcon={<CaretRightIcon size={16} color="#667085" />}
                size="s"
                styleType="transparent"
                style={{ marginLeft: 4, padding: 0 }}
                aria-label={`Continue ${course.course.name}`}
                accessibilityHint="Continue course"
              />
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  degreeProgressCard: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#0084FF1A",
    borderRadius: 16,
  },
  degreeName: {
    marginLeft: 8,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: "600",
    color: "#101828",
  },
  overallProgressContainer: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  overallProgress: {
    fontWeight: "400",
    color: "#475467",
  },
  overallProgressValue: {
    fontWeight: "700",
    color: "#0084FF",
  },
  progressBarContainer: {
    height: 8,
    marginBottom: 10,
    backgroundColor: "#D0D5DD",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBar: {
    height: 8,
    backgroundColor: "#007CC1",
    borderRadius: 4,
  },
  enrolled: {
    marginBottom: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  courseCard: {
    marginBottom: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
  },
  courseName: {
    color: "#101828",
  },
  courseDetails: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  progressBarContainerSmall: {
    width: 106,
    marginBottom: 0,
    marginRight: 10,
  },
});
